import { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAdminUpdateConsultantHourPackage,
  getAdminGetConsultantHourPackagesQueryKey,
} from '@/api/auth/admin/admin';
import type { ConsultantHourPackageResponse } from '@/api/auth/missionImpactAuthAPI.schemas';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { logger } from '@/services/logger';
import {
  consultantHourPackageSchema,
  type ConsultantHourPackageForm,
} from '@/schemas/consultant-hour-package';
import { ConsultantHourPackageFields } from './ConsultantHourPackageFields';

interface EditConsultantHourPackageDialogProps {
  productId: string;
  /** The package being edited; the dialog is open while this is set. */
  package: ConsultantHourPackageResponse | null;
  onClose: () => void;
}

/**
 * Dialog for editing a consultant hour package's name, hours and sort order. The Stripe price is
 * fixed; to change it, add a new package and deactivate this one.
 */
export function EditConsultantHourPackageDialog({
  productId,
  package: pkg,
  onClose,
}: EditConsultantHourPackageDialogProps) {
  const queryClient = useQueryClient();

  const form = useForm<ConsultantHourPackageForm>({
    resolver: zodResolver(consultantHourPackageSchema),
  });
  const { handleSubmit, reset } = form;

  useEffect(() => {
    if (pkg) {
      reset({ displayName: pkg.displayName, hours: pkg.hours, sortOrder: pkg.sortOrder });
    }
  }, [pkg, reset]);

  const { mutate, isPending } = useAdminUpdateConsultantHourPackage({
    mutation: {
      onSuccess: () => {
        toast.success('Consultant hour package updated');
        onClose();
        queryClient.invalidateQueries({
          queryKey: getAdminGetConsultantHourPackagesQueryKey(productId),
        });
      },
      onError: (error: any) => {
        logger.error(error, {
          context: 'UpdateConsultantHourPackage',
          productId,
          packageId: pkg?.id,
        });
        toast.error('Failed to update package. ' + (error.response?.data?.detail || error.message));
      },
    },
  });

  const onSubmit = useCallback(
    (data: ConsultantHourPackageForm) => {
      if (pkg) {
        mutate({ productId, packageId: pkg.id, data });
      }
    },
    [mutate, pkg, productId]
  );

  return (
    <Dialog open={!!pkg} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Consultant Hour Package</DialogTitle>
          <DialogDescription>
            Hours changes apply to checkouts started after saving. Checkouts already in progress
            credit the hours shown when they started.
          </DialogDescription>
        </DialogHeader>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <ConsultantHourPackageFields />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
